/**
 * Client-side presentation helpers.
 *
 * These back the visual design system in `app.postcss` — initials avatars,
 * friendly timestamps. Keep display logic here rather than duplicating it
 * across page components.
 */

import site from './site';

/**
 * Where an uploaded catalogue image is actually served from (§4.1).
 *
 * The server hands out `/api/media/{uuid}` — a **server-relative** path, because it has no idea
 * what origin the client is on. Every other request this app makes goes through
 * `site.backend.baseURL`, and the moment that is set to a separate API host — the ordinary
 * production shape — a bare `/api/…` in an `<img src>` resolves against the *client's* origin
 * instead and 404s. The failure is a broken image, which nothing alerts on and which is
 * indistinguishable on screen from a product that simply has no logo: the icon is "not visible
 * anywhere" and every page still looks fine.
 *
 * Absolute URLs are returned untouched, so a server that ever starts handing out a full URL — or a
 * `data:` preview held by `ImageField` before an upload completes — is not mangled by this.
 */
/**
 * The list a drop produced: `key` lifted out and put back where `targetKey` was (§14).
 *
 * Splice-out-then-insert rather than a swap, because a drag across four rows is a *move*, not an
 * exchange with whatever happens to be at the far end — swapping would leave the two rows between
 * them untouched and the order nothing like the one dropped onto.
 *
 * Unknown keys and a no-op drop return the list unchanged, so a stale row from a list that has
 * re-rendered under the pointer cannot scramble the order.
 */
export function moveKeyBefore(keys: string[], key: string, targetKey: string): string[] {
	const from = keys.indexOf(key);
	const to = keys.indexOf(targetKey);
	if (from === -1 || to === -1 || from === to) {
		return keys;
	}

	const next = [...keys];
	next.splice(from, 1);
	next.splice(to, 0, key);
	return next;
}

/**
 * The list one keyboard press produced: `key` moved by `delta` places, clamped at both ends (§14).
 *
 * Clamped rather than wrapped: an admin holding ArrowUp on the top row expects nothing to happen,
 * and a row that reappeared at the bottom of a ladder would have quietly become the most capable
 * tier in the line.
 */
export function moveKeyBy(keys: string[], key: string, delta: number): string[] {
	const from = keys.indexOf(key);
	if (from === -1) {
		return keys;
	}

	const to = Math.min(Math.max(from + delta, 0), keys.length - 1);
	if (to === from) {
		return keys;
	}

	const next = [...keys];
	next.splice(from, 1);
	next.splice(to, 0, key);
	return next;
}

export function mediaURL(url: string | null | undefined): string {
	if (url === null || url === undefined || url.length === 0) return '';
	if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//')) return url;
	return `${site.backend.baseURL}${url}`;
}

/** Anything with a human name on it — the app's `User`, or an AbstractID token. */
interface NameLike {
	firstName?: string;
	lastName?: string;
	displayName?: string;
	/** AbstractID generates one for an account created from an email alone; it is a real name. */
	username?: string;
	email?: string;
}

/**
 * What to call somebody, given a token that may carry no name at all.
 *
 * The client-side twin of `AuthService.displayNameFor`, and needed for the same reason: an account
 * AbstractID created from an email address alone (Design §14) has empty `firstName` and
 * `lastName`, so `` `${firstName} ${lastName}` `` renders as a lone space — a blank line above the
 * email in the sidebar card that looks like a rendering fault rather than a name nobody gave.
 *
 * Falls back name → username → email, exactly as the server does, so the same customer is called
 * the same thing on the storefront and in the admin customer list.
 */
export function displayNameOf(user: NameLike | null | undefined): string {
	if (!user) return '';

	const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
	if (fullName.length > 0) return fullName;

	return user.username || user.displayName || user.email || '';
}

/** Two-letter initials for an avatar. Falls back through display name, then email. */
export function getUserInitials(user: NameLike | null | undefined): string {
	if (!user) return '?';

	if (user.firstName && user.lastName) {
		return (user.firstName[0] + user.lastName[0]).toUpperCase();
	}

	const fallback = user.displayName || user.email || '';
	return fallback.substring(0, 2).toUpperCase() || '?';
}

// Tailwind palette for initials-only avatars. Listed as full string literals (not built
// up at runtime) so the Tailwind JIT scanner picks them up. `!` prefix forces priority
// over Flowbite Avatar's default `bg-gray-100 dark:bg-gray-600`.
const AVATAR_COLOR_PALETTE: string[] = [
	'!bg-violet-100 !text-violet-700 dark:!bg-violet-900/40 dark:!text-violet-300',
	'!bg-amber-100 !text-amber-700 dark:!bg-amber-900/40 dark:!text-amber-300',
	'!bg-emerald-100 !text-emerald-700 dark:!bg-emerald-900/40 dark:!text-emerald-300',
	'!bg-blue-100 !text-blue-700 dark:!bg-blue-900/40 dark:!text-blue-300',
	'!bg-pink-100 !text-pink-700 dark:!bg-pink-900/40 dark:!text-pink-300',
	'!bg-cyan-100 !text-cyan-700 dark:!bg-cyan-900/40 dark:!text-cyan-300',
	'!bg-rose-100 !text-rose-700 dark:!bg-rose-900/40 dark:!text-rose-300',
	'!bg-indigo-100 !text-indigo-700 dark:!bg-indigo-900/40 dark:!text-indigo-300',
	'!bg-teal-100 !text-teal-700 dark:!bg-teal-900/40 dark:!text-teal-300'
];

/**
 * Picks a stable Tailwind bg+text colour pair for an initials-only avatar, keyed on
 * `seed` so the same person renders the same hue everywhere in the app. Empty seed
 * falls back to the first palette entry — deterministic, never random.
 */
export function getAvatarColorClasses(seed: string | undefined | null): string {
	if (!seed) return AVATAR_COLOR_PALETTE[0];

	let hash = 0;
	for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
	return AVATAR_COLOR_PALETTE[Math.abs(hash) % AVATAR_COLOR_PALETTE.length];
}

const MONTH_SHORT_NAMES: readonly string[] = [
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
	'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * Formats a date as "27 Apr 2026, 10:42" — the locale-independent absolute timestamp
 * used wherever a full date is shown.
 */
export function formatFriendlyDate(date: Date | string): string {
	const value = typeof date === 'string' ? new Date(date) : date;

	const day = String(value.getDate()).padStart(2, '0');
	const month = MONTH_SHORT_NAMES[value.getMonth()];
	const year = value.getFullYear();
	const hour = String(value.getHours()).padStart(2, '0');
	const minute = String(value.getMinutes()).padStart(2, '0');
	return `${day} ${month} ${year}, ${hour}:${minute}`;
}

const MONTH_LONG_NAMES: readonly string[] = [
	'January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Formats a date as "3 September 2026" — a day with no clock on it.
 *
 * The account area is full of deadlines a customer acts on ("update your payment method by 3
 * September to keep access") and dates a subscription rolls over on. A timestamp to the minute
 * there reads as precision the store does not actually promise, and buries the part that matters
 * — the day — in noise.
 */
export function formatCalendarDate(date: Date | string): string {
	const value = typeof date === 'string' ? new Date(date) : date;
	return `${value.getDate()} ${MONTH_LONG_NAMES[value.getMonth()]} ${value.getFullYear()}`;
}

/** Short relative age ("just now", "5m ago", "3d ago"), falling back to an absolute date. */
export function formatRelativeDate(date: Date | string): string {
	const value = typeof date === 'string' ? new Date(date) : date;
	const diff = Date.now() - value.getTime();

	const minutes = Math.floor(diff / 60000);
	const hours = Math.floor(diff / 3600000);
	const days = Math.floor(diff / 86400000);

	if (minutes < 1) return 'just now';
	if (minutes < 60) return `${minutes}m ago`;
	if (hours < 24) return `${hours}h ago`;
	if (days < 7) return `${days}d ago`;
	return formatFriendlyDate(value);
}

// --- Money (Design §10) ------------------------------------------------------------------------

import type { StorefrontCurrency } from './AppTypes';

/**
 * Formats an integer **minor-unit** amount using the currency row's own `minorUnitDigits`.
 *
 * There is deliberately no default for the divisor. `amount / 100` is correct for USD and wrong
 * by a factor of a hundred for JPY, whose `minorUnitDigits` is 0 — and it is wrong *silently*,
 * because every figure on the page still looks like money. That is why `/api/configuration`
 * returns whole `Currency` rows rather than ISO codes: the number of decimal places is data, and
 * a client that has only the code has no way to know it.
 *
 * Every price the storefront renders goes through here, via `PriceDisplay`.
 *
 * @param locale Left undefined in the app so grouping follows the visitor's own locale; passed
 *   explicitly by tests, which cannot depend on the runtime's.
 */
export function formatMoney(
	amountMinor: number,
	currency: StorefrontCurrency,
	locale?: string
): string {
	const major = Math.abs(amountMinor) / Math.pow(10, currency.minorUnitDigits);
	const formatted = new Intl.NumberFormat(locale, {
		minimumFractionDigits: currency.minorUnitDigits,
		maximumFractionDigits: currency.minorUnitDigits
	}).format(major);

	/*
	 * **The sign goes before the symbol**, not between it and the digits.
	 *
	 * Formatting the signed value directly produces `$-19.00`, which is the spelling that makes a
	 * reader stop and check whether the figure is a typo — and negative money is ordinary here
	 * rather than exotic: every credit note is negative throughout (§4.6), every refund row on the
	 * financial report is, and so is any proration that credits unused time. The server's invoice
	 * renderer has always done it this way; this is the same rule on the same figures.
	 */
	const sign = amountMinor < 0 ? '-' : '';
	return `${sign}${currency.symbol}${formatted}`;
}

/**
 * Minor units as the decimal an admin actually types: `1900` in EUR is `"19.00"`, in JPY `"1200"`.
 *
 * The pair below exists because the admin forms used to take minor units directly, which is
 * correct, unambiguous, and wrong for a person: "1900" for nineteen euros is a figure you have to
 * *convert* every time you read or write it, and the mistake it invites — typing 19 — is a plan
 * priced at nineteen cents that looks entirely plausible in a table.
 *
 * Both directions are **string arithmetic**, never `× 100`. `19.99 * 100` is `1998.9999999999998`
 * in IEEE 754, and the currencies here have between zero and three decimal places, so there is no
 * single divisor to be careless with anyway.
 */
export function majorUnitsFrom(amountMinor: number, minorUnitDigits: number): string {
	if (minorUnitDigits <= 0) {
		return String(amountMinor);
	}
	const sign = amountMinor < 0 ? '-' : '';
	const digits = Math.abs(amountMinor).toString().padStart(minorUnitDigits + 1, '0');
	return `${sign}${digits.slice(0, -minorUnitDigits)}.${digits.slice(-minorUnitDigits)}`;
}

/**
 * The decimal an admin typed, back to minor units. Null when the text is not a money amount.
 *
 * Null rather than 0 for unparseable input: a form that reads a typo as zero creates a free plan,
 * and nothing about the result looks wrong. A comma is accepted as the decimal separator, because
 * half of Europe types one and refusing it is a bug report rather than a lesson.
 *
 * Extra precision is **rounded**, not truncated — `0.005` in a two-place currency is a cent, and
 * dropping it would price a rounding-down error into the catalogue.
 */
export function minorUnitsFrom(value: string, minorUnitDigits: number): number | null {
	const trimmed = value.trim().replace(',', '.');
	if (trimmed === '' || !/^-?\d*(\.\d*)?$/.test(trimmed) || /^-?\.?$/.test(trimmed)) {
		return null;
	}

	const negative = trimmed.startsWith('-');
	const [whole, fractionRaw = ''] = trimmed.replace('-', '').split('.');
	const digits = Math.max(0, minorUnitDigits);
	// One digit past the currency's precision, so the rounding decision is in the string rather
	// than in a float division that may not represent the number being rounded.
	const fraction = `${fractionRaw}${'0'.repeat(digits + 1)}`.slice(0, digits + 1);
	const scaled = Number(`${whole === '' ? '0' : whole}${fraction}`);
	if (!Number.isFinite(scaled)) {
		return null;
	}
	const minor = Math.round(scaled / 10);
	return negative ? -minor : minor;
}

/** How a recurring price reads beside its figure: `/month`, `/3 months`, or nothing one-time. */
export function formatInterval(interval: string | null, intervalCount: number): string {
	if (interval === null) return '';
	return intervalCount === 1 ? `/${interval}` : `/${intervalCount} ${interval}s`;
}

/**
 * Which sidebar link a location should light up.
 *
 * Flowbite's `SidebarItem` compares `href === activeUrl` **exactly**, so the value handed to
 * `Sidebar` has to be a link that actually exists in the nav rather than the raw location.
 * Truncating to the first path segment — the obvious way to make a detail page light its list —
 * collapses `/Settings/Providers` onto `/Settings`, so a section with sub-items highlights its own
 * index and nothing else.
 *
 * The rule that satisfies both: **the longest declared href that the location sits under.**
 * `/Settings/Providers` picks itself, `/Customers/<uuid>` picks `/Customers`, and `/Settings`
 * lights only for the store settings page itself. `/` is matched exactly, or the overview would
 * swallow every page in the console.
 *
 * Falls back to the location itself, which simply lights nothing.
 */
export function activeSidebarPath(location: string, hrefs: string[]): string {
	let best = '';

	for (const href of hrefs) {
		const isMatch =
			href === '/'
				? location === '/'
				: location === href || location.startsWith(`${href}/`);

		if (isMatch && href.length > best.length) {
			best = href;
		}
	}

	return best === '' ? location : best;
}

/**
 * Where a `/Settings/Admin/*` URL lives now.
 *
 * Administration was a Settings sub-section until it became the console's root navigation, so
 * every admin page moved up two segments — and the store settings themselves, which were
 * `/Settings/Admin/Settings/*`, landed on `/Settings/*`. The overview moved to `/`. This is the
 * whole of the mapping behind `LegacyAdminRedirect`, kept pure so it can be tested without a
 * router; a path outside `/Settings/Admin` is returned unchanged.
 */
export function adminPathFromLegacy(location: string): string {
	const legacy = '/Settings/Admin';
	if (location !== legacy && !location.startsWith(`${legacy}/`)) return location;

	// Dropping the prefix is the whole rule: `/Settings/Admin/Settings/Tax` becomes `/Settings/Tax`
	// by the same cut that turns `/Settings/Admin/Customers/<uuid>` into `/Customers/<uuid>`.
	const rest = location.slice(legacy.length).replace(/\/+$/, '');
	return rest === '' ? '/' : rest;
}

/**
 * Whether the Store issued its own document for this invoice (Design §4.6).
 *
 * A non-empty string, never `!== null`. The field is nullable, so `!== null` looks right — and it
 * is `true` for `undefined`, which is what a payload missing the field reads as. That reading has
 * already produced a receipt claiming invoice number `undefined` with an unrenderable PDF attached.
 */
export function hasIssuedInvoiceDocument(invoice: { documentNumber?: string | null }): boolean {
	return typeof invoice.documentNumber === 'string' && invoice.documentNumber !== '';
}

/**
 * The number printed on the copy of this invoice the customer actually has.
 *
 * There are **two** number columns and they are not interchangeable: `documentNumber` is ours,
 * allocated at issue, and `number` is the payment provider's. An invoice serves our PDF if and only
 * if it carries the first — so a screen that renders `number` on an invoice we issued shows a
 * reference that appears nowhere on the document the customer is holding. Nothing fails; the two
 * sides simply disagree, and it surfaces as a support conversation where neither party can find
 * what the other is looking at.
 *
 * The uuid fallback is a draft invoice, which has no number of either kind yet. It is still a handle
 * somebody can quote, and it is visibly not an invoice number.
 */
export function invoiceReference(invoice: {
	uuid: string;
	number?: string | null;
	documentNumber?: string | null;
}): string {
	if (hasIssuedInvoiceDocument(invoice)) {
		return invoice.documentNumber as string;
	}
	// The same non-empty test, for the same reason: an empty provider number is not a number.
	if (typeof invoice.number === 'string' && invoice.number !== '') {
		return invoice.number;
	}
	return invoice.uuid.slice(0, 8);
}

/**
 * Whether a catalogue product has been **archived**, as opposed to never published (Design §4.1).
 *
 * `isActive: false` means two different things on a product, and telling them apart is the whole
 * of this helper. A product is created inactive and `publishedAt` is stamped the first time it is
 * activated — so an inactive product that has never been published is a **draft** somebody is in
 * the middle of authoring, while one that *has* been published and then deactivated is archived.
 * Treating the two alike would make a product vanish from the catalogue screen the moment it was
 * created, which is the one moment its author is certain to be looking at it.
 *
 * The `publishedAt` test is a non-empty **string**, never `!== null`: the field is nullable, so
 * `!== null` looks right and is `true` for `undefined` — which is what a DTO missing the field
 * reads as, and would file every draft in the store under archived.
 */
export function isArchivedProduct(product: {
	isActive: boolean;
	publishedAt?: string | null;
}): boolean {
	return (
		!product.isActive && typeof product.publishedAt === 'string' && product.publishedAt !== ''
	);
}

/**
 * The same question for a product line (Design §4.1).
 *
 * A family has no draft state — it is created active, and `archiveFamily` is the only thing that
 * deactivates it — so inactive *is* archived here, with no second condition. It stays a named
 * helper rather than an inline `!isActive` so the two screens that hide archived rows ask the
 * question the same way, and so the day a family grows a draft state there is one place to change.
 */
export function isArchivedFamily(family: { isActive: boolean }): boolean {
	return !family.isActive;
}

/**
 * The service period an invoice covers, as one label, and **empty where there is not one**.
 *
 * One implementation because two screens render it — the account invoice list and the subscription
 * page — and a period that reads one way in a list and another on the page it links to is a
 * question with two answers.
 *
 * The guard that earns its keep is the **last** one: a range whose ends render as the same date is
 * not a range on screen, whatever the instants behind it say. Three separate things produce that,
 * and only one of them is fixed by holding better data. A one-time purchase has no service period
 * at all. A provider's `period_start`/`period_end` are the window its invoice *items* were
 * collected in, which for an invoice finalised on creation is a single instant — that is the
 * "1 September 2026 – 1 September 2026" this exists to stop, and the Stripe adapter now derives
 * the period from the lines instead. And an invoice stored before the lines were snapshotted keeps
 * a collection window nothing can recompute, which for a window inside one day renders exactly the
 * same way. Formatting both ends and comparing the **rendered** strings is what covers all three,
 * because it asks the question the reader is actually asking.
 */
export function invoicePeriodLabel(invoice: {
	periodStart?: string | null;
	periodEnd?: string | null;
}): string {
	if (invoice.periodStart == null || invoice.periodEnd == null) {
		return '';
	}
	const start = formatCalendarDate(invoice.periodStart);
	const end = formatCalendarDate(invoice.periodEnd);
	return start === end ? '' : `${start} – ${end}`;
}

/**
 * What an invoice's state is called on screen (Design §9.6).
 *
 * The provider's `status` is not the answer on its own, and this is the reason the helper exists:
 * **a refund does not move it**. Stripe refunds the *charge*, so an invoice that has been paid back
 * in full is still `paid` — and every screen in the app said "Paid" beside money the customer had
 * already had returned, which is the one thing an invoice list is read to check. It is not a stale
 * value that will settle; it is the provider correctly describing something else.
 *
 * So the refund is a fact of its own (`refundedAmount`, summed server-side over the refunds that
 * have moved or are moving) and this is the single place the two are combined. One implementation
 * because there are four screens — the account list, the subscription page and two admin screens —
 * and a customer being told "Refunded" while support reads "Paid" is a support conversation with
 * nothing to resolve it.
 *
 * What was actually settled is `amountPaid`, falling back to `total`: a partial refund has to read
 * as partial, and comparing against the wrong figure turns a full refund of a discounted invoice
 * into "Partially refunded". Refunds are compared with `>=` rather than `===` for the same reason
 * `hasIssuedInvoiceDocument` tests for a non-empty string — a refund topped up by a goodwill credit
 * can exceed the invoice, and that is more refunded, not less.
 */
export function invoiceStatusLabel(invoice: {
	status: string;
	refundedAmount?: number | null;
	amountPaid?: number | null;
	total?: number | null;
}): string {
	const refunded = invoice.refundedAmount ?? 0;
	const settled = invoice.amountPaid ?? invoice.total ?? 0;

	if (refunded > 0 && settled > 0) {
		return refunded >= settled ? 'Refunded' : 'Partially refunded';
	}

	return INVOICE_STATUS_COPY[invoice.status] ?? invoice.status;
}

/**
 * The question a purchase term asked and the answer the buyer gave, as one line (§4.7).
 *
 * **Both halves or neither.** The question is snapshotted beside its answer precisely so a term
 * relabelled since cannot restate what somebody gave as an answer to something they were never
 * asked — so a row carrying one without the other renders nothing at all rather than an answer
 * under today's wording. `chk_clickwrap_acceptances_input` makes that impossible in the database,
 * and a server predating the columns sends neither.
 *
 * Here rather than in the component that first needed it, because two screens render an acceptance
 * now — the per-purchase panel and the customer-wide register — and two audiences reading the same
 * evidence differently is a support conversation with nothing left to resolve it. The *layouts*
 * differ, deliberately; the rules that decide what an acceptance says may not.
 */
export function acceptanceAnswer(acceptance: {
	inputLabel?: string | null;
	inputValue?: string | null;
}): string | null {
	const label = (acceptance.inputLabel ?? '').trim();
	const value = (acceptance.inputValue ?? '').trim();
	return label.length === 0 || value.length === 0 ? null : `${label}: ${value}`;
}

/**
 * Whether the term this accepted still reads the way it did, in one word (§4.7).
 *
 * `edited` and `removed` are **not faults and are worded so they do not read as one**: the customer
 * agreed to what they were shown, and the catalogue has moved since. It is surfaced at all because
 * support otherwise compares the snapshot against the live term and finds a difference with no
 * explanation.
 *
 * `null` is the ordinary case — the words on the acceptance are still the words on the term.
 */
export function acceptanceVersionNote(acceptance: {
	clickwrapUUID: string | null;
	isCurrentVersion: boolean | null;
}): 'edited' | 'removed' | null {
	if (acceptance.isCurrentVersion === false) return 'edited';
	if (acceptance.clickwrapUUID === null) return 'removed';
	return null;
}

/** What `acceptanceVersionNote` says on screen. One wording, for the reason the helper is shared. */
export const ACCEPTANCE_VERSION_COPY: Record<'edited' | 'removed', string> = {
	edited: 'Term edited since',
	removed: 'Term since removed'
};

/**
 * The provider's own invoice statuses, in words.
 *
 * Exported so a filter dropdown offers the same names the rows show. `open` is the one a customer
 * has to act on, which is why it is not "Unpaid": that reads as a fault rather than as a bill.
 */
export const INVOICE_STATUS_COPY: Record<string, string> = {
	draft: 'Draft',
	open: 'Awaiting payment',
	paid: 'Paid',
	void: 'Void',
	uncollectible: 'Written off'
};

/**
 * The intervals a subscription price may bill on, as the admin forms offer them.
 *
 * One list, exported, because two forms create prices — this panel and the first-price block on the
 * product form — and an interval added to one of them and not the other is a catalogue where what
 * you can author depends on which screen you happened to start from.
 *
 * **Daily is for testing**, and it earns its place: every other interval takes a month or a year to
 * produce a second invoice, so renewal, dunning, the grace cap and expiry are all unobservable in a
 * development store without a fake clock. It is not hidden behind a flag, because a store that
 * genuinely sells by the day is one this should serve.
 */
export const BILLING_INTERVALS = [
	{ value: 'day', name: 'Daily', description: 'Bills every day. Mostly for testing renewals and dunning quickly.' },
	{ value: 'month', name: 'Monthly', description: 'Bills every month on the same day.' },
	{ value: 'year', name: 'Yearly', description: 'Bills every year on the same date.' }
];

/**
 * How far a visit got, in words (§12.4).
 *
 * One list, because two screens name these — the analytics explorer and a customer's own visits on
 * the support page — and a stage called "Engaged the flow" on one and "Started buying" on the other
 * is two people describing the same row differently in the same conversation.
 *
 * Indexed by the server's ordinal, and an unknown index answers the first rather than an empty
 * cell: a stage this list has not caught up with is still a visit that happened.
 */
const ANALYTICS_STAGE_NAMES = [
	'Visited',
	'Browsed',
	'Viewed a product',
	'Engaged the flow',
	'Signed in',
	'Reached payment',
	'Purchased'
];

export function analyticsStageName(stage: number): string {
	return ANALYTICS_STAGE_NAMES[stage] ?? ANALYTICS_STAGE_NAMES[0];
}

/**
 * How long a visit lasted, in words.
 *
 * A list that showed only when a visit *started* made a three-second visit from yesterday read
 * "18h ago" under a heading called Visits, which looks like something still running. The start is
 * worth knowing and stays; this is the answer to the different question sitting beside it.
 */
export function analyticsVisitDuration(seconds: number): string {
	if (seconds < 1) return 'a moment';
	if (seconds < 60) return `${seconds}s`;
	if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.round((seconds % 3600) / 60);
	return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

/**
 * What to say about the part of a range the raw events no longer cover (§12.8).
 *
 * One sentence, in one place, because two panels say it and a store told two different things about
 * the same boundary is a store nobody trusts about either. It names the date rather than the
 * setting: an operator reading a chart needs to know where the line falls, not what it is called.
 */
export function analyticsRetentionNote(
	coverage: { cutoff: string; partial: boolean } | undefined,
	answerable: boolean
): string {
	if (coverage === undefined || !coverage.partial) return '';
	const from = formatCalendarDate(coverage.cutoff);
	return answerable
		? `Before ${from} this is drawn from the daily archive, which keeps totals rather than individual visits — so it cannot be filtered.`
		: `Only from ${from}. Events older than that have been deleted under the retention setting, and this figure cannot be recovered from the archive — it is missing rather than zero.`;
}

/**
 * What one `allowedIPs` entry covers, read exactly as the server's `parseCIDR` reads it: an IPv4
 * or IPv6 address, optionally `/prefix` within its family's width. Null for anything else.
 *
 * The server refuses an entry it cannot parse; this is what lets the editor say so on the row,
 * and say what a valid one actually admits — `/16` where `/24` was meant is a typo that reads as
 * configured and opens the endpoint to 65,536 addresses.
 */
export interface CIDRRange {
	family: 4 | 6;
	prefix: number;
	/** The entry's network address with the host bits cleared, in its family's notation. */
	network: string;
	/** IPv4 only: the first and last address covered. */
	first: string | null;
	last: string | null;
	/** How many addresses it admits. A bigint, because a /0 in IPv6 is 2^128. */
	count: bigint;
	/** True when the entry set bits below its prefix — `203.0.113.7/24` means `203.0.113.0/24`. */
	hostBitsSet: boolean;
}

function ipv4ToBigInt(address: string): bigint | null {
	if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(address)) return null;
	const parts = address.split('.').map(Number);
	if (parts.some(part => part > 255)) return null;
	return parts.reduce((total, part) => (total << 8n) + BigInt(part), 0n);
}

function ipv6ToBigInt(address: string): bigint | null {
	if (!address.includes(':') || (address.match(/::/g) ?? []).length > 1) return null;
	const [head, tail] = address.split('::') as [string, string | undefined];
	const headParts = head === '' ? [] : head.split(':');
	const tailParts = tail === undefined || tail === '' ? [] : tail.split(':');
	if (tail === undefined && headParts.length !== 8) return null;
	const missing = 8 - headParts.length - tailParts.length;
	if (missing < 0 || (tail !== undefined && missing === 0)) return null;
	let value = 0n;
	for (const group of [...headParts, ...Array(missing).fill('0'), ...tailParts]) {
		if (!/^[0-9a-fA-F]{1,4}$/.test(group)) return null;
		value = (value << 16n) + BigInt(parseInt(group, 16));
	}
	return value;
}

function bigIntToIPv4(value: bigint): string {
	return [24n, 16n, 8n, 0n].map(shift => String((value >> shift) & 255n)).join('.');
}

function bigIntToIPv6(value: bigint): string {
	const groups = Array.from({ length: 8 }, (_unused, index) =>
		((value >> BigInt((7 - index) * 16)) & 0xffffn).toString(16)
	);
	// The longest run of zero groups collapses to `::`, as every tool an admin compares against does.
	let bestStart = -1;
	let bestLength = 0;
	for (let start = 0; start < 8; ) {
		if (groups[start] !== '0') {
			start++;
			continue;
		}
		let end = start;
		while (end < 8 && groups[end] === '0') end++;
		if (end - start > bestLength && end - start > 1) {
			bestStart = start;
			bestLength = end - start;
		}
		start = end;
	}
	if (bestStart === -1) return groups.join(':');
	return `${groups.slice(0, bestStart).join(':')}::${groups.slice(bestStart + bestLength).join(':')}`;
}

export function parseCIDRRange(entry: string): CIDRRange | null {
	const trimmed = entry.trim();
	const slash = trimmed.indexOf('/');
	const address = slash === -1 ? trimmed : trimmed.slice(0, slash);
	const prefixText = slash === -1 ? null : trimmed.slice(slash + 1);

	const v4 = ipv4ToBigInt(address);
	const v6 = v4 === null ? ipv6ToBigInt(address) : null;
	if (v4 === null && v6 === null) return null;
	const family: 4 | 6 = v4 !== null ? 4 : 6;
	const bits = family === 4 ? 32 : 128;
	const value = (v4 ?? v6) as bigint;

	if (prefixText !== null && !/^\d{1,3}$/.test(prefixText)) return null;
	const prefix = prefixText === null ? bits : Number(prefixText);
	if (prefix > bits) return null;

	const hostBits = BigInt(bits - prefix);
	const network = (value >> hostBits) << hostBits;
	const last = network + (1n << hostBits) - 1n;
	return {
		family,
		prefix,
		network: family === 4 ? bigIntToIPv4(network) : bigIntToIPv6(network),
		first: family === 4 ? bigIntToIPv4(network) : null,
		last: family === 4 ? bigIntToIPv4(last) : null,
		count: 1n << hostBits,
		hostBitsSet: network !== value
	};
}

/**
 * What a purchase ends on (§14) — `Product.postPurchase` / `ProductFamily.postPurchase` — as the
 * admin form holds it (strings throughout) and as it is sent (`postPurchaseInput`).
 */
export interface PostPurchaseDraft {
	message: string;
	links: { label: string; url: string }[];
}

/** The server's rule for a post-purchase link: http(s), or a path on this store. */
export function isPostPurchaseURL(url: string): boolean {
	return /^https?:\/\/[^\s]+$/i.test(url) || /^\/(?!\/)[^\s]*$/.test(url);
}

export function postPurchaseDraftOf(
	content: { message: string | null; links: { label: string; url: string }[] } | null | undefined
): PostPurchaseDraft {
	return { message: content?.message ?? '', links: (content?.links ?? []).map(link => ({ ...link })) };
}

/** Blank rows dropped; a block that says nothing is sent as null, which clears it. */
export function postPurchaseInput(
	draft: PostPurchaseDraft
): { message: string | null; links: { label: string; url: string }[] } | null {
	const message = draft.message.trim() === '' ? null : draft.message.trim();
	const links = draft.links
		.map(link => ({ label: link.label.trim(), url: link.url.trim() }))
		.filter(link => link.label !== '' || link.url !== '');
	return message === null && links.length === 0 ? null : { message, links };
}
